import { existsSync, mkdirSync, readFileSync, rmSync, writeFileSync } from 'node:fs';
import { dirname, join, relative, resolve, sep } from 'node:path';
import type { Catalog } from './catalog';
import type { ResolvedConfig } from './config';
import { withLineEndings } from './eol';
import { collectParams, renderParamType } from './params';
import { byCodeUnit } from './text';
import { includeTypes } from './tsconfig';
import { warnOnce } from './warn';

export const VIRTUAL_ID = 'virtual:verbaly';

// the header every generated d.ts opens with: a file without it was written by a person
export const DTS_HEADER = '// generated by verbaly: do not edit';

export interface RuntimeModuleOptions {
  localeImport?: (locale: string) => string;
  extraExports?: string;
  icu?: boolean;
  relative?: boolean;
  inlineCatalog?: boolean;
}

// only under inlineCatalog: read the blob `verbaly render` left, and take the next page's own
const SLICE_HELPERS = `function readSlice(root, path) {
  const locale = readPath({ supported: locales, path });
  if (!locale || locale === sourceLocale) return undefined;
  const messages = inlineMessages(root);
  return messages ? { locale, messages } : undefined;
}

export function adoptPage(options) {
  const slice = readSlice(options?.root, options?.path);
  if (slice) v.addMessages(slice.locale, slice.messages, { partial: true });
  return slice ? slice.locale : undefined;
}

`;

export function generateRuntimeModule(
  cfg: ResolvedConfig,
  options: RuntimeModuleOptions = {},
): string {
  const importPath = options.localeImport ?? ((locale: string) => `${VIRTUAL_ID}/locale/${locale}`);
  const others = cfg.locales.filter((locale) => locale !== cfg.sourceLocale);
  const src = JSON.stringify(cfg.sourceLocale);
  const loaders = others
    .map((locale) => `  ${JSON.stringify(locale)}: () => import('${importPath(locale)}'),`)
    .join('\n');

  // named in the import list only when a catalog uses them, so otherwise they tree-shake out
  const icu = options.icu ? ',\n  parseIcu' : '';
  const rel = options.relative ? ',\n  relativeFormatter' : '';
  const slice = options.inlineCatalog ? ',\n  inlineMessages' : '';
  const slices = options.inlineCatalog ? SLICE_HELPERS : '';

  return `import {
  createVerbaly,
  localeFromPath as readPath,
  localePath as writePath,
  switchLocale as runSwitch${icu}${rel}${slice},
} from 'verbaly';
import source from '${importPath(cfg.sourceLocale)}';

export const sourceLocale = ${src};
export const locales = ${JSON.stringify(cfg.locales)};
export const routing = ${JSON.stringify(cfg.routing)};

// bound to this project: the locale set, the source and the routing mode are never passed in
export function localePath(locale, options) {
  return writePath(locale, { ...options, supported: locales, sourceLocale, routing });
}

export function localeFromPath(options) {
  return readPath({ ...options, supported: locales });
}

// identity: it exists so the compiler can see keys that live in a module instead of at a call site
export function defineKeys(keys) {
  return keys;
}

// the whole switch a language control needs, in either mode: catalog or navigation, then persistence
export function switchLocale(locale, options) {
  return runSwitch(v, locale, { supported: locales, sourceLocale, routing, ...options });
}

const localeLoaders = {
${loaders}
};

// raw catalog access: SSR integrations serialize it across the client boundary
export async function loadMessages(locale) {
  if (locale === ${src}) return source;
  // own entries only: a locale named constructor must be a miss, not the Object function
  const loader = Object.prototype.hasOwnProperty.call(localeLoaders, locale)
    ? localeLoaders[locale]
    : undefined;
  return loader ? (await loader()).default : {};
}

${slices}// per-request/per-instance factory (SSR): the singleton below is browser/SPA-only
export function createInstance(options) {${
    options.inlineCatalog ? '\n  const page = readSlice();' : ''
  }
  return createVerbaly({
    locale: ${options.inlineCatalog ? `page ? page.locale : ${src}` : src},
    fallback: ${src},
    messages: ${
      options.inlineCatalog
        ? `page ? { [${src}]: source, [page.locale]: page.messages } : { [${src}]: source }`
        : `{ [${src}]: source }`
    },
    loaders: localeLoaders,${options.inlineCatalog ? '\n    partial: page ? [page.locale] : undefined,' : ''}${options.icu ? '\n    icu: parseIcu,' : ''}
    ...options,${
      options.relative
        ? '\n    // merged, not spread over: passing your own formatters must not drop this one' +
          '\n    formatters: { relative: relativeFormatter, ...options?.formatters },'
        : ''
    }
  });
}

// the no-FOUC contract in one call: fresh instance + catalog awaited before render
export async function createRequestInstance(locale) {
  const instance = createInstance({ locale });
  await instance.loadLocale(locale);
  return instance;
}

const v = createInstance();

// a hook with no provider above it (a test, a story) reads this one; a server never registers it
if (typeof document !== 'undefined') globalThis[Symbol.for('verbaly.app')] = v;

export const verbaly = v;
export const t = v.t;

export function getLocale() {
  return v.locale;
}

export function subscribe(listener) {
  return v.subscribe(listener);
}

export async function setLocale(locale) {
  await v.loadLocale(locale);
  v.setLocale(locale);
}
${options.extraExports ?? ''}`;
}

export function generateLocaleModule(catalog: Catalog): string {
  // "__proto__": in an object literal sets the prototype; computed, it stays a key like any other
  const json = JSON.stringify(catalog).replace(/([{,])"__proto__":/, '$1["__proto__"]:');
  return `export default ${json};\n`;
}

export interface DtsOptions {
  inlineCatalog?: boolean;
  locales?: string[];
}

export function generateDts(catalog: Catalog, options: DtsOptions = {}): string {
  const lines: string[] = [];
  const locales = options.locales?.length
    ? [...options.locales]
        .sort(byCodeUnit)
        .map((locale) => JSON.stringify(locale))
        .join(' | ')
    : 'string';
  for (const [key, message] of Object.entries(catalog).sort(([a], [b]) => byCodeUnit(a, b))) {
    const params = collectParams(message);
    if (params.size === 0) {
      lines.push(`    ${JSON.stringify(key)}: never;`);
    } else {
      const fields = [...params.entries()]
        .map(([name, types]) => `${JSON.stringify(name)}: ${renderParamType(types)}`)
        .join('; ');
      lines.push(`    ${JSON.stringify(key)}: { ${fields} };`);
    }
  }

  return `${DTS_HEADER}
// biome-ignore-all lint: generated by verbaly
// biome-ignore-all format: generated by verbaly
// prettier-ignore
declare module 'virtual:verbaly' {
  export interface VerbalyMessages {
${lines.join('\n')}
  }
  export type VerbalyKey = keyof VerbalyMessages & string;
  export type VerbalyLocale = ${locales};

  export type VerbalyKeyTree = VerbalyKey | { readonly [name: string]: VerbalyKeyTree };
  export function defineKeys<const T extends VerbalyKeyTree>(keys: T): T;

  export const verbaly: import('verbaly').Verbaly;

  export const sourceLocale: VerbalyLocale;
  export const locales: string[];
  export const routing: import('verbaly').Routing;
  export function localePath(
    locale: string,
    options?: { path?: string; base?: string },
  ): string;
  export function localeFromPath(options?: {
    path?: string;
    base?: string;
  }): string | undefined;
  export function loadMessages(locale: string): Promise<Record<string, string>>;${
    options.inlineCatalog
      ? '\n  export function adoptPage(options?: {' +
        '\n    root?: ParentNode;' +
        '\n    path?: string;' +
        '\n  }): string | undefined;'
      : ''
  }
  export function createInstance(
    options?: import('verbaly').VerbalyOptions,
  ): import('verbaly').Verbaly;
  export function createRequestInstance(locale: string): Promise<import('verbaly').Verbaly>;

  // a literal key must exist; a string, a template with data in it or a union of keys is data
  export const t: {
    <K extends VerbalyKey | TemplateStringsArray | (string & {})>(
      first: K extends TemplateStringsArray
        ? K
        : {} extends Record<K & string, 1>
          ? K
          : K extends VerbalyKey ? K : VerbalyKey,
      ...rest: [K] extends [never]
        ? [params?: import('verbaly').Params]
        : [K] extends [(K extends unknown ? (key: K) => void : never) extends (key: infer One) => void ? One : never]
          ? K extends TemplateStringsArray
            ? unknown[]
            : {} extends Record<K & string, 1>
              ? [params?: import('verbaly').Params]
              : K extends VerbalyKey
                ? [VerbalyMessages[K]] extends [never] ? [] : [VerbalyMessages[K]]
                : unknown[]
          : [params?: import('verbaly').Params]
    ): string;
    id(key: string): (strings: TemplateStringsArray, ...values: unknown[]) => string;
  };

  export function setLocale(locale: string): Promise<void>;
  export function switchLocale(
    locale: string,
    options?: Omit<
      import('verbaly').SwitchLocaleOptions,
      'routing' | 'supported' | 'sourceLocale'
    >,
  ): Promise<void>;
  export function getLocale(): string;
  export function subscribe(listener: () => void): () => void;

  // nested, so it augments verbaly: at the top of a script it would replace the real module
  module 'verbaly' {
    interface Register {
      t: typeof t;
      locale: VerbalyLocale;
    }
  }
}

// prettier-ignore
declare module 'virtual:verbaly/locale/*' {
  const messages: Record<string, string>;
  export default messages;
}
`;
}

// the d.ts a project gets: what the config says about it is passed the same way everywhere
export function projectDtsOptions(cfg: ResolvedConfig): DtsOptions {
  return { inlineCatalog: cfg.render.inlineCatalog === true, locales: cfg.locales };
}

export interface DtsWrite {
  file: string;
  changed: boolean;
}

// unchanged writes are skipped: a rewritten d.ts churns the consumer's TS server
export function writeDts(
  cfg: ResolvedConfig,
  catalog: Catalog,
  file?: string | false,
): DtsWrite | undefined {
  // the config decides unless a caller names a file: dts false means no file anywhere
  const target = file === undefined ? cfg.dts : file;
  if (target === false) return undefined;
  let existing: string | undefined;
  try {
    existing = readFileSync(target, 'utf8');
  } catch {
    // new file
  }
  const content = withLineEndings(generateDts(catalog, projectDtsOptions(cfg)), existing);
  const changed = existing !== content;
  if (changed) {
    mkdirSync(dirname(target), { recursive: true });
    writeFileSync(target, content);
  }
  removeRootDts(cfg, target);
  // .verbaly/ is a dot folder, and TypeScript only reads one that tsconfig names
  if (resolve(target).startsWith(resolve(cfg.root, '.verbaly') + sep)) {
    // generated and never committed: the .gitignore withVerbaly writes next to its modules
    const ignore = join(cfg.root, '.verbaly', '.gitignore');
    if (!existsSync(ignore)) writeFileSync(ignore, '*\n');
    const state = includeTypes(cfg.root, target);
    const entry = relative(cfg.root, target).replaceAll('\\', '/');
    if (state === 'added') {
      warnOnce(`added "${entry}" to the include of tsconfig.json, so TypeScript reads the types`);
    } else if (state === 'unreadable') {
      warnOnce(`tsconfig.json could not be read, so add "${entry}" to its include yourself`);
    }
  }
  return { file: target, changed };
}

// the types moved out of the root: the old file would declare the same module a second time
function removeRootDts(cfg: ResolvedConfig, target: string): void {
  const old = join(cfg.root, 'verbaly.d.ts');
  if (resolve(target) === resolve(old)) return;
  let head: string;
  try {
    head = readFileSync(old, 'utf8').slice(0, DTS_HEADER.length);
  } catch {
    return;
  }
  // a verbaly.d.ts someone wrote by hand is theirs
  if (head !== DTS_HEADER) return;
  rmSync(old);
  const moved = relative(cfg.root, target).replaceAll('\\', '/');
  warnOnce(
    `the types live in ${moved} now, so the verbaly.d.ts Verbaly wrote at the root was removed`,
  );
}
